import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Chip
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import CasinoIcon from '@mui/icons-material/Casino';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ClearAllDataDialog from '../ClearAllDataDialog/ClearAllDataDialog';
import './Header.scss';

const Header = ({ sessionName = null, sessionId = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);



  const handleHomeClick = () => {
    navigate('/');
  };

  const handleDashboardClick = () => {
    if (sessionId) {
      navigate(`/session/${sessionId}`);
    } else {
      navigate('/');
    }
  };

  const handleExpensesClick = () => {
    if (sessionId) {
      navigate(`/session/${sessionId}/expenses`);
    } else {
      navigate('/expenses');
    }
  };

  const handleSettlementClick = () => {
    if (sessionId) {
      navigate(`/session/${sessionId}/settlement`);
    } else {
      navigate('/settlement');
    }
  };

  const handleSettingsClick = (event) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  const handleClearAllDataClick = () => {
    handleSettingsClose();
    setClearAllDialogOpen(true);
  };

  const handleLogout = async () => {
    handleSettingsClose();
    await logout();
    navigate('/login');
  };


  const isDashboard = sessionId 
    ? location.pathname === `/session/${sessionId}` || location.pathname === `/session/${sessionId}/`
    : location.pathname === '/';
  const isExpensesPage = sessionId 
    ? location.pathname === `/session/${sessionId}/expenses`
    : location.pathname === '/expenses';
  const isSettlementPage = sessionId 
    ? location.pathname === `/session/${sessionId}/settlement`
    : location.pathname === '/settlement';

  return (
    <>
      <AppBar position="static" className="header" elevation={0}>
        <Toolbar className="header-toolbar">
          {/* Logo Section - Only show in session pages */}
          {sessionId && (
            <Box className="header-logo">
              <Box className="logo-icon">♠</Box>
              <Typography variant="h6" className="logo-text">
                Home Game
              </Typography>
              {sessionName && (
                <Chip 
                  label={sessionName} 
                  size="small" 
                  className="session-chip"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}
          
          <Box className="header-nav">
            <Button
              onClick={handleDashboardClick}
              className={`nav-button ${isDashboard ? 'active' : ''}`}
              color="inherit"
            >
              Dashboard
            </Button>
            <Button
              onClick={handleExpensesClick}
              className={`nav-button ${isExpensesPage ? 'active' : ''}`}
              color="inherit"
            >
              Expenses
            </Button>
            <Button
              onClick={handleSettlementClick}
              className={`nav-button ${isSettlementPage ? 'active' : ''}`}
              color="inherit"
            >
              Settlement
            </Button>
          </Box>
          
          <Box className="header-actions">
            <Box className="header-subtitle">
              <Typography variant="body2" color="text.secondary">
                {sessionName ? `Session: ${sessionName}` : user ? `Welcome, ${user.name || user.email}` : 'Track your poker buy-ins and expenses with style'}
              </Typography>
            </Box>
            
            {sessionId && (
              <IconButton
                onClick={handleHomeClick}
                className="home-button"
                aria-label="home"
                title="Back to Sessions"
              >
                <HomeIcon />
              </IconButton>
            )}
            
            <IconButton
              onClick={handleSettingsClick}
              className="settings-button"
              aria-label="settings"
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={handleSettingsClose}
        className="settings-menu"
      >
        {sessionId && (
          <MenuItem onClick={handleHomeClick} className="home-menu-item">
            <ListItemIcon>
              <HomeIcon color="primary" />
            </ListItemIcon>
            <Typography color="primary">Back to Sessions</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={handleClearAllDataClick} className="clear-data-item">
          <ListItemIcon>
            <DeleteForeverIcon color="error" />
          </ListItemIcon>
          <Typography color="error">Clear All Session Data</Typography>
        </MenuItem>
        <MenuItem onClick={handleLogout} className="logout-item">
          <ListItemIcon>
            <LogoutIcon color="primary" />
          </ListItemIcon>
          <Typography color="primary">Logout</Typography>
        </MenuItem>
      </Menu>

      <ClearAllDataDialog
        open={clearAllDialogOpen}
        onClose={() => setClearAllDialogOpen(false)}
      />
    </>
  );
};

export default Header;